import { useCallback, useEffect, useMemo, useState } from 'react'
import { QUESTIONS, QUESTIONS_PER_PAGE } from '../data/questions'
import type { AnswerMap, AnswerValue, Step } from '../types/assessment'

const STORAGE_KEY = 'relational-archetypes:progress'
const TOTAL_PAGES = Math.ceil(QUESTIONS.length / QUESTIONS_PER_PAGE)

type StoredProgress = {
  answers: AnswerMap
  page: number
}

function loadProgress(): StoredProgress | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredProgress
    if (!parsed || typeof parsed !== 'object' || !parsed.answers) return null
    return parsed
  } catch {
    return null
  }
}

/**
 * Owns assessment state: answers, pagination, and step.
 * Answers are mirrored to localStorage so a refresh, an accidental back
 * gesture, or a dropped connection never costs someone their progress.
 */
export function useAssessment() {
  const [step, setStep] = useState<Step>('intro')
  const [answers, setAnswers] = useState<AnswerMap>({})
  const [page, setPage] = useState(0)
  const [validationMessage, setValidationMessage] = useState('')
  const [hasSavedProgress, setHasSavedProgress] = useState(false)

  useEffect(() => {
    const stored = loadProgress()
    if (stored && Object.keys(stored.answers).length > 0) {
      setAnswers(stored.answers)
      setPage(Math.min(stored.page, TOTAL_PAGES - 1))
      setHasSavedProgress(true)
    }
  }, [])

  useEffect(() => {
    if (step === 'results') return
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers, page }))
    } catch {
      /* storage unavailable — in-memory state still works */
    }
  }, [answers, page, step])

  // Guard against losing a partly finished assessment.
  useEffect(() => {
    const inProgress = step === 'questions' || step === 'email'
    if (!inProgress) return

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [step])

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [step, page])

  const answeredCount = Object.keys(answers).length

  const pageQuestions = useMemo(
    () => QUESTIONS.slice(page * QUESTIONS_PER_PAGE, page * QUESTIONS_PER_PAGE + QUESTIONS_PER_PAGE),
    [page],
  )

  const answerQuestion = useCallback((id: number, value: AnswerValue) => {
    setAnswers((prev) => ({ ...prev, [id]: value }))
    setValidationMessage('')
  }, [])

  const start = useCallback((options?: { fresh?: boolean }) => {
    if (options?.fresh) {
      setAnswers({})
      setPage(0)
      setHasSavedProgress(false)
    }
    setValidationMessage('')
    setStep('questions')
  }, [])

  const goNext = useCallback(() => {
    const unanswered = pageQuestions.some((question) => answers[question.id] === undefined)
    if (unanswered) {
      setValidationMessage('Please answer all six statements on this page to continue.')
      return
    }
    setValidationMessage('')
    if (page < TOTAL_PAGES - 1) setPage((prev) => prev + 1)
    else setStep('email')
  }, [answers, page, pageQuestions])

  const goBack = useCallback(() => {
    setValidationMessage('')
    if (page > 0) setPage((prev) => prev - 1)
    else setStep('intro')
  }, [page])

  const backToQuestions = useCallback(() => {
    setStep('questions')
    setPage(TOTAL_PAGES - 1)
  }, [])

  const clearProgress = useCallback(() => {
    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch {
      /* no-op */
    }
  }, [])

  return {
    step,
    setStep,
    answers,
    answerQuestion,
    page,
    pageQuestions,
    totalPages: TOTAL_PAGES,
    totalQuestions: QUESTIONS.length,
    answeredCount,
    validationMessage,
    hasSavedProgress,
    start,
    goNext,
    goBack,
    backToQuestions,
    clearProgress,
  }
}
